/**
 * SimpleMediaOrderPage - Create CD/DVD/Blu-ray orders with a simple form
 * No configurator needed - just basic order info and file uploads
 */
import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../contexts/TranslationContext';
import { Header } from '../components/Header';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import CatalogueNumberInput from '../components/CatalogueNumberInput';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import axios from 'axios';
import { 
    ArrowLeft, 
    ArrowRight, 
    Disc,
    Upload,
    File,
    Trash2,
    Loader2,
    CheckCircle
} from 'lucide-react';
import { MediaDiscIcon } from '../components/MediaDiscIcons';

import { API_BASE } from '../lib/apiBase';
const API = API_BASE + '/api';

const PRODUCT_TYPE_CONFIG = {
    cd: {
        labelKey: 'simple_order.cd.label',
        labelDefault: 'CD Production',
        iconType: 'cd',
        color: 'bg-blue-50 border-blue-200',
        descKey: 'simple_order.cd.desc',
        descDefault: 'Standard CD replication order',
    },
    dvd: {
        labelKey: 'simple_order.dvd.label',
        labelDefault: 'DVD Production',
        iconType: 'dvd',
        color: 'bg-purple-50 border-purple-200',
        descKey: 'simple_order.dvd.desc',
        descDefault: 'DVD video or data disc order',
    },
    bluray: {
        labelKey: 'simple_order.bluray.label',
        labelDefault: 'Blu-ray Production',
        iconType: 'bluray',
        color: 'bg-indigo-50 border-indigo-200',
        descKey: 'simple_order.bluray.desc',
        descDefault: 'High-definition Blu-ray disc order',
    },
};

export default function SimpleMediaOrderPage() {
    const { user } = useAuth();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    
    // Get product type from URL
    const productType = searchParams.get('type') || 'cd';
    const config = PRODUCT_TYPE_CONFIG[productType] || PRODUCT_TYPE_CONFIG.cd;
    const configLabel = t(config.labelKey, config.labelDefault);
    const configDesc = t(config.descKey, config.descDefault);
    
    // Form state. Title is mandatory; artist is optional (separate field —
    // replaces legacy combined "Artist - Title" UX). PO number also captured
    // here so it's available in the same flow as vinyl orders.
    const [title, setTitle] = useState('');
    const [artist, setArtist] = useState('');
    const [catalogueNumber, setCatalogueNumber] = useState('');
    const [catalogueValid, setCatalogueValid] = useState(true);
    const [customerPoNumber, setCustomerPoNumber] = useState('');
    const [quantity, setQuantity] = useState('');
    const [notes, setNotes] = useState('');
    
    // Submission state
    const [isCreating, setIsCreating] = useState(false);
    const [orderId, setOrderId] = useState(null);
    
    // Validate form
    const isValid = title.trim() && quantity && parseInt(quantity) > 0 && catalogueValid;
    
    // Create simple media order
    const handleCreateOrder = async () => {
        if (!isValid) {
            toast.error(t('common.fill_required', 'Please fill in all required fields'));
            return;
        }

        setIsCreating(true);
        try {
            const token = localStorage.getItem('token');
            const response = await axios.post(
                `${API}/orders`,
                {
                    title: title.trim(),
                    artist: artist.trim() || null,
                    catalogue_number: catalogueNumber.trim() || null,
                    customer_po_number: customerPoNumber.trim() || null,
                    product_type: productType,
                    quantity: parseInt(quantity),
                    notes: notes.trim() || null,
                    description: `${configLabel} - ${quantity} units`,
                },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            const newOrderId = response.data.id;
            setOrderId(newOrderId);

            toast.success(t('simple_order.created_toast', 'Order created successfully'));

            navigate(`/orders/${newOrderId}/files`);
        } catch (error) {
            const message = error.response?.data?.detail || t('simple_order.create_failed', 'Failed to create order');
            toast.error(message);
        } finally {
            setIsCreating(false);
        }
    };
    
    return (
        <div className="min-h-screen bg-slate-50">
            <Header />
            <main className="max-w-3xl mx-auto p-6">
                {/* Back Button */}
                <button
                    onClick={() => navigate('/dashboard')}
                    className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-6 transition-colors"
                >
                    <ArrowLeft className="h-5 w-5" />
                    <span className="text-sm font-medium">{t('common.back_to_dashboard', 'Back to Dashboard')}</span>
                </button>

                {/* Header */}
                <div className={`rounded-xl p-6 mb-6 border ${config.color}`}>
                    <div className="flex items-center gap-4">
                        <div className="text-4xl">
                            <MediaDiscIcon type={config.iconType} size={48} />
                        </div>
                        <div>
                            <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight leading-tight text-slate-900">{configLabel}</h1>
                            <p className="text-sm text-slate-600">{configDesc}</p>
                        </div>
                    </div>
                </div>

                {/* Order Form */}
                <Card>
                    <CardHeader>
                        <CardTitle className="text-lg">{t('simple_order.info_title', 'Order Information')}</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-6">
                        {/* Title */}
                        <div className="space-y-2">
                            <Label htmlFor="title">
                                {t('order.title_label', 'Title')} <span className="text-red-500">*</span>
                            </Label>
                            <Input
                                id="title"
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                placeholder={t('order.title_placeholder', 'Enter album or project title')}
                                data-testid="simple-order-title"
                            />
                        </div>

                        {/* Artist (optional separate field) */}
                        <div className="space-y-2">
                            <Label htmlFor="artist">
                                {t('order.artist_label', 'Artist')} <span className="text-slate-400 text-xs">{t('common.optional_parenthesis', '(optional)')}</span>
                            </Label>
                            <Input
                                id="artist"
                                value={artist}
                                onChange={(e) => setArtist(e.target.value)}
                                placeholder={t('order.artist_placeholder', 'Artist name')}
                                data-testid="simple-order-artist"
                            />
                        </div>

                        {/* Catalogue Number */}
                        <div className="space-y-2">
                            <CatalogueNumberInput
                                label={t('order.catalogue_number_label', 'Catalogue Number')}
                                value={catalogueNumber}
                                onChange={setCatalogueNumber}
                                onValidityChange={setCatalogueValid}
                                placeholder={t('order.catalogue_number_placeholder', 'e.g., CAT-2025-001')}
                                data-testid="simple-order-catalogue"
                            />
                            <p className="text-xs text-slate-500">
                                {t('order.catalogue_number_help', 'Your internal catalogue reference (optional)')}
                            </p>
                        </div>

                        {/* PO Number */}
                        <div className="space-y-2">
                            <Label htmlFor="customerPoNumber">
                                {t('order.po_number_label', 'PO Number')} <span className="text-slate-400 text-xs">{t('common.optional_parenthesis', '(optional)')}</span>
                            </Label>
                            <Input
                                id="customerPoNumber"
                                value={customerPoNumber}
                                onChange={(e) => setCustomerPoNumber(e.target.value)}
                                placeholder="PO-12345"
                                data-testid="simple-order-po"
                            />
                        </div>

                        {/* Quantity */}
                        <div className="space-y-2">
                            <Label htmlFor="quantity">
                                {t('order.quantity_label', 'Quantity')} <span className="text-red-500">*</span>
                            </Label>
                            <Input
                                id="quantity"
                                type="number"
                                min="1"
                                value={quantity}
                                onChange={(e) => setQuantity(e.target.value)}
                                placeholder={t('order.quantity_placeholder', 'Enter quantity')}
                                data-testid="simple-order-quantity"
                            />
                            <p className="text-xs text-slate-500">
                                {t('order.quantity_help', 'Number of units to produce')}
                            </p>
                        </div>

                        {/* Notes */}
                        <div className="space-y-2">
                            <Label htmlFor="notes">
                                {t('order.notes_label', 'Notes')}
                            </Label>
                            <Textarea
                                id="notes"
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                placeholder={t('order.notes_placeholder', 'Any special instructions or requirements...')}
                                rows={4}
                                data-testid="simple-order-notes"
                            />
                        </div>

                        {/* Info Box */}
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                            <p className="text-sm text-blue-800">
                                <strong>{t('simple_order.next_steps_title', 'Next steps:')}</strong> {t('simple_order.next_steps_body', "After creating this order, you'll be able to upload your master files, artwork, and tracklist in the order workspace.")}
                            </p>
                        </div>

                        {/* Submit Button */}
                        <div className="flex justify-end pt-4">
                            <Button
                                onClick={handleCreateOrder}
                                disabled={!isValid || isCreating}
                                className="min-w-[200px]"
                                data-testid="create-simple-order-btn"
                            >
                                {isCreating ? (
                                    <>
                                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                        {t('simple_order.creating', 'Creating Order...')}
                                    </>
                                ) : (
                                    <>
                                        {t('simple_order.submit', 'Create Order')}
                                        <ArrowRight className="h-4 w-4 ml-2" />
                                    </>
                                )}
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            </main>
        </div>
    );
}
